import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMethods } from 'react-use';
import { css } from '@emotion/css';
import { differenceInCalendarDays, format, startOfDay } from 'date-fns';
import addDays from 'date-fns/addDays';
import formatISO from 'date-fns/formatISO';
import isSameDay from 'date-fns/isSameDay';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosBack, ArrowIosForward, Calendar2Outline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import {
  DatePickerDate,
  DateVariant,
  DividerProps,
  DividerSeparator,
  Loader,
  PickersDay,
  PickersDayProps,
  Stack,
  Typography,
} from '@lyearn/ui';
import InlineDatePicker from '@/components/InlineDatePicker';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import {
  CalendarWidgetType,
  ContentPerformanceEdge,
  ContentPerformanceEntity,
  PageComponentTypeEnum,
  WidgetTypeEnum,
} from '@/types/schema';
import { getCurrentTimeZoneValue } from '@/utils/timezone';

import { useContentsByDueDateQuery } from '../../graphql/queries/generated/contentsByDueDate';
import { HomepageLeafComponentProps, HomeWidgetProps } from '../../types';
import { DEFAULT_LAYOUTS, WidgetDimensions } from '../../utils/layouts';
import ListEmptyState from '../HomepageList/components/ListEmptyState';
import ListItem from '../HomepageList/components/ListItem';
import { widgetBackgroundGradient } from '../HomepageWelcomeAndStats/styles';
import WidgetContainer from '../HomepageWidgetList/components/WidgetContainer';
import { AdditionalWidgetProps } from '../types';
import WidgetBox from '../WidgetBox';
import {
  CALENDAR_LAYOUT_CONFIG_MAP,
  calendarMobileStyles,
  createMethods,
  getCalendarStyles,
  getInitialState,
} from './utils';

export interface HomepageCalendarStates {
  isUserSelectedDate: boolean;
  selectedCalendarDate: Date;
  startingCalendarMonth: Date;
}

export const verticalDivider = css`
  width: 0.1rem;
  height: undefined;
`;

export const horizontalDivider = css`
  height: 0.1rem;
`;

const VerticalDivider = () => (
  <div className={cn(verticalDivider, 'mx-24 mt-20 mb-12 bg-interactive-secondary-hover')} />
);
const DIVIDER_PROPS: DividerProps = { className: 'bg-border-dark', orientation: 'horizontal' };
const DEFAULT_LAYOUT = DEFAULT_LAYOUTS[PageComponentTypeEnum.Calendar];

type HomepageCalendarProps = HomepageLeafComponentProps & AdditionalWidgetProps;
const HomepageCalendar: React.FC<HomepageCalendarProps> = React.memo((props) => {
  const { pageComponent, readOnly, hideBorders, defaultGradientColor } = props;

  const { id: userId } = useUserContext();
  const { t } = useTranslation(['Session']);
  const navigation = useNavigation();
  const { isMobile, isDesktop } = useScreen();
  const adaptedInitialContents = readOnly
    ? ((pageComponent.widget as any)?.widget as CalendarWidgetType)?.contents
    : (pageComponent.widget as CalendarWidgetType)?.contents;
  const initialContents = adaptedInitialContents?.edges?.map(
    (edge: ContentPerformanceEdge) => edge.node,
  );
  const [currentContents, setCurrentContents] = useState<ContentPerformanceEntity[] | undefined>(
    initialContents,
  );
  const { props: pageComponentPropsString } = pageComponent;
  let pageComponentProps;
  try {
    pageComponentProps = JSON.parse(pageComponentPropsString || '');
  } catch (e) {
    pageComponentProps = {};
  }
  // @ts-ignore
  const layoutProp = pageComponent?.layout || pageComponentProps.layout;
  const layout = layoutProp?.width && layoutProp?.height ? layoutProp : DEFAULT_LAYOUT;
  const { height: heightDimension, width: widthDimension } = layout;

  const gradientColor = (pageComponentProps as any)?.gradientColor || defaultGradientColor;

  const gradientStyles = widgetBackgroundGradient(gradientColor, isMobile);

  const [calendarState, calendarMethods] = useMethods(createMethods, getInitialState());
  const { selectedCalendarDate } = calendarState;
  const { setSelectedCalendarDate, setStartingCalendarMonth } = calendarMethods;

  const onMonthChange = useCallback(
    (date: DatePickerDate) => {
      date && setStartingCalendarMonth(date);
    },
    [setStartingCalendarMonth],
  );

  const [{ fetching, data }, refetch] = useContentsByDueDateQuery({
    pause: isSameDay(selectedCalendarDate, startOfDay(new Date())),
    variables: {
      filters: {
        userIds: [userId],
        dueDate: {
          timezone: getCurrentTimeZoneValue(),
          absolute: {
            startTime: formatISO(selectedCalendarDate.setHours(0, 0, 0, 0)),
            endTime: formatISO(selectedCalendarDate.setHours(23, 59, 59, 999)),
          },
        },
      },
    },
  });

  const today = useMemo(() => startOfDay(new Date()), []);
  const currentSelectedDate = useMemo(
    () => startOfDay(selectedCalendarDate),
    [selectedCalendarDate],
  );

  useEffect(() => {
    const today = startOfDay(new Date());
    const sameDay = isSameDay(today as Date, selectedCalendarDate);
    if (data && !sameDay) {
      const adaptedContent = data.contentPerformances?.edges?.map(
        (edge) => edge.node,
      ) as ContentPerformanceEntity[];
      setCurrentContents(adaptedContent);
    } else if (sameDay) {
      setCurrentContents(initialContents);
    }
  }, [data, currentSelectedDate]);

  const onChange = useCallback(
    (date: any) => {
      if (date) {
        date.setHours(0, 0, 0, 0);
        setSelectedCalendarDate(date);

        refetch({
          variables: {
            filters: {
              userIds: [userId],
              dueDate: {
                timezone: getCurrentTimeZoneValue(),
                absolute: {
                  startTime: formatISO(selectedCalendarDate.setHours(0, 0, 0, 0)),
                  endTime: formatISO(selectedCalendarDate.setHours(23, 59, 59, 999)),
                },
              },
            },
          },
        });
      }
    },
    [currentSelectedDate, setSelectedCalendarDate],
  );

  const goToNextDay = useCallback(() => {
    const nextDate = addDays(selectedCalendarDate, 1);
    setSelectedCalendarDate(nextDate);

    const startTime = formatISO(nextDate.setHours(0, 0, 0, 0));
    const endTime = formatISO(nextDate.setHours(23, 59, 59, 999));

    refetch({
      variables: {
        filters: {
          userIds: [userId],
          dueDate: {
            timezone: getCurrentTimeZoneValue(),
            absolute: {
              startTime,
              endTime,
            },
          },
        },
      },
      requestPolicy: 'network-only',
    });
  }, [selectedCalendarDate, setSelectedCalendarDate, refetch, userId]);

  const goToPreviousDay = useCallback(() => {
    const prevDate = addDays(selectedCalendarDate, -1);
    setSelectedCalendarDate(prevDate);

    const startTime = formatISO(prevDate.setHours(0, 0, 0, 0));
    const endTime = formatISO(prevDate.setHours(23, 59, 59, 999));

    refetch({
      variables: {
        userIds: [userId],
        dueDate: {
          timezone: getCurrentTimeZoneValue(),
          absolute: {
            startTime,
            endTime,
          },
        },
      },
    });
  }, [selectedCalendarDate, setSelectedCalendarDate, refetch, userId]);

  const mobileStyles = calendarMobileStyles();

  const calendarStyles =
    heightDimension && widthDimension
      ? getCalendarStyles({
          ...CALENDAR_LAYOUT_CONFIG_MAP[`${heightDimension}-${widthDimension}`],
          transparentBackground: gradientColor?.length,
        })
      : '';

  const renderDay = (
    day: DatePickerDate,
    selectedDates: DatePickerDate[],
    pickersDayProps: PickersDayProps<DatePickerDate>,
  ) => {
    if (!day) {
      return <PickersDay {...pickersDayProps} />;
    }
    const dateString = format(day as Date, 'yyyy-MM-dd');
    const dayFlag =
      (pageComponent.widget as CalendarWidgetType)?.groupContentPerformanceByDate?.dateFlags?.find(
        (config) => config.date === dateString,
      )?.flag || false;
    const isPrevDay = differenceInCalendarDays(day, today) < 0;
    const isSelectedDate = selectedCalendarDate && isSameDay(day as Date, selectedCalendarDate);
    if (dayFlag && !isSelectedDate) {
      const isOverdue = dayFlag && !isSelectedDate && isPrevDay;
      let isFutureDue = dayFlag && !isSelectedDate && !isPrevDay;
      return (
        <PickersDay
          className={cn(
            '!border !border-solid',
            isOverdue ? ' !border-[#FCA5A5] !text-[#B91C1C]' : '',
            isFutureDue ? '!border-[#93C5FD] !text-[#0065FF]' : '',
          )}
          {...pickersDayProps}
        />
      );
    } else {
      return (
        <PickersDay
          className={cn(isPrevDay && !isSelectedDate ? '!text-[#999999]' : '')}
          {...pickersDayProps}
        />
      );
    }
  };

  const seeAllRedirect = () => {
    navigation.replace(`/widget/${(pageComponent.widget as HomeWidgetProps).id}`);
  };

  if (!(pageComponent.widget as HomeWidgetProps)?.type) {
    return null;
  }

  const showListItems =
    ((heightDimension === WidgetDimensions.L || heightDimension === WidgetDimensions.M) &&
      (widthDimension === WidgetDimensions.L || widthDimension === WidgetDimensions.XL)) ||
    isMobile;

  const listItemMaxWidth = isDesktop ? 'max-w-[504px]' : 'max-w-[380px]';

  const transparentBackground = Boolean(gradientColor?.length);

  return (
    <WidgetContainer
      key={(pageComponent as any).id}
      className={cn(
        'bg-bg-primary',
        isMobile ? 'rounded-0' : 'rounded-24',
        readOnly && isMobile ? '-mx-20 px-12' : '',
        transparentBackground ? gradientStyles : '',
      )}
      layout={isMobile ? { height: WidgetDimensions.M, width: WidgetDimensions.XS } : layout}
      pageComponentId={(pageComponent as any).id}>
      <WidgetBox
        className={cn(
          readOnly && isMobile ? '!px-0' : '',
          transparentBackground ? '!bg-bg-transparent' : '',
        )}
        hideBorders={hideBorders}
        showSeeAll={(currentContents?.length || 0) > 4}
        transparentBackground={transparentBackground}
        widgetId={(pageComponent.widget as any)?.id}>
        <Stack
          className={cn(
            'rounded-24 h-full w-full overflow-hidden',
            isMobile ? 'py-16' : 'px-6',
            transparentBackground ? '!bg-bg-transparent' : '',
          )}>
          {!isMobile ? (
            <InlineDatePicker
              className={cn(
                calendarStyles,
                '!pb-0 h-full w-full',
                isMobile ? mobileStyles : '',
                showListItems ? listItemMaxWidth : '',
                transparentBackground ? '!bg-bg-transparent' : '',
              )}
              pickerProps={{
                onMonthChange,
                variant: DateVariant.Date,
                selectedTime: selectedCalendarDate,
                onChange,
                renderDay,
              }}
              showActionButtons={false}
              showSelectedTime={false}
            />
          ) : null}
          {showListItems && !isMobile ? <VerticalDivider /> : null}
          {showListItems ? (
            <Stack
              className={cn(
                isMobile ? '!w-full' : 'w-full pt-20 pb-12 pr-12',
                transparentBackground ? '!bg-bg-transparent' : '',
              )}
              direction="col">
              <Stack
                alignItems="center"
                className={transparentBackground ? '!bg-bg-transparent' : ''}
                justifyContent="between">
                <Stack alignItems="center">
                  <Calendar2Outline height={24} width={24} />
                  <Typography className="px-12" variant={isMobile ? 'heading-m' : 'heading-l'}>
                    {format(selectedCalendarDate, 'dd MMM yyyy')}
                  </Typography>
                </Stack>
                {isMobile || !((currentContents?.length || 0) > 4) ? null : (
                  <Typography
                    className="text-link-internal cursor-pointer"
                    variant="label-01"
                    onClick={seeAllRedirect}>
                    {t('Session:SeeAll')}
                  </Typography>
                )}
                {isMobile ? (
                  <Stack gapX={2}>
                    <Stack className="p-4" onClick={goToPreviousDay}>
                      <ArrowIosBack fillColorToken="icon-secondary" height={28} width={28} />
                    </Stack>
                    <Stack className="p-4" onClick={goToNextDay}>
                      <ArrowIosForward fillColorToken="icon-secondary" height={28} width={28} />
                    </Stack>
                  </Stack>
                ) : null}
              </Stack>

              {fetching ? (
                <Stack className="h-full w-full">
                  <Loader className="mx-auto my-auto" size={36} />
                </Stack>
              ) : currentContents?.length ? (
                <DividerSeparator
                  className={cn(
                    'overflow-y-auto',
                    isMobile ? 'rounded-16 border border-border mt-16' : '',
                    isMobile && (transparentBackground ? '!bg-bg-transparent' : 'bg-bg-primary'),
                  )}
                  direction="col"
                  dividerProps={DIVIDER_PROPS}>
                  {currentContents?.map((entity: ContentPerformanceEntity, index: number) => {
                    if (isMobile && index > 2) {
                      return null;
                    }
                    return (
                      <ListItem
                        key={index}
                        isCalendar
                        containerClassName="!p-12"
                        entity={entity.entity}
                        layout={{ height: heightDimension, width: widthDimension }}
                        widgetType={WidgetTypeEnum.Contents}
                      />
                    );
                  })}
                  {isMobile && currentContents?.length ? (
                    <Stack
                      className="bg-interactive-secondary-hover py-8 px-16"
                      onClick={seeAllRedirect}>
                      <Typography className="text-link-internal" variant="label-02">
                        {t('Session:SeeAll')}
                      </Typography>
                    </Stack>
                  ) : null}
                </DividerSeparator>
              ) : (
                <ListEmptyState
                  transparentBackground={transparentBackground}
                  widgetType={WidgetTypeEnum.Calendar}
                />
              )}
            </Stack>
          ) : null}
        </Stack>
      </WidgetBox>
    </WidgetContainer>
  );
});

export default React.memo(HomepageCalendar);
